import { DateTime } from 'luxon';
import type { TFunction } from 'i18next';

const LOCALE_BY_LANG: Record<string, string> = {
  en: 'en',
  ar: 'ar',
  fr: 'fr',
  lb: 'en',
};

export function getLocale(lang?: string | null): string {
  return LOCALE_BY_LANG[lang || 'en'] || 'en';
}

/** Locale-aware number formatting (Arabic-Indic numerals for Arabic). */
export function formatNumber(value: number, lang?: string | null): string {
  return new Intl.NumberFormat(getLocale(lang)).format(value);
}

export function formatDateTime(value: string | null | undefined, lang?: string | null): string {
  if (!value) return '';
  const dt = DateTime.fromISO(value).setLocale(getLocale(lang));
  return dt.isValid ? dt.toLocaleString({ ...DateTime.DATETIME_MED, timeZoneName: 'short' }) : value;
}

export function formatDateTimeShort(value: string | null | undefined, lang?: string | null): string {
  if (!value) return '';
  const dt = DateTime.fromISO(value).setLocale(getLocale(lang));
  return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : value;
}

export function formatDateTimeWithWeekday(value: string | null | undefined, lang?: string | null): string {
  if (!value) return '';
  const dt = DateTime.fromISO(value).setLocale(getLocale(lang));
  return dt.isValid
    ? dt.toLocaleString({ weekday: 'long', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : value;
}

export function formatTime(value: string | null | undefined, lang?: string | null): string {
  if (!value) return '';
  const dt = DateTime.fromISO(value).setLocale(getLocale(lang));
  return dt.isValid ? dt.toLocaleString(DateTime.TIME_SIMPLE) : value;
}

export function formatRelativeTime(value: string | null | undefined, lang?: string | null): string {
  if (!value) return '';
  const dt = DateTime.fromISO(value).setLocale(getLocale(lang));
  if (!dt.isValid) return value;
  return dt.toRelative({ locale: getLocale(lang), style: 'short' }) || dt.toLocaleString(DateTime.DATETIME_MED);
}

export function formatSyncAge(value: string | null | undefined, lang: string | null | undefined, t: TFunction): string {
  if (!value) return '';
  const dt = DateTime.fromISO(value).setLocale(getLocale(lang));
  if (!dt.isValid) return '';

  const seconds = Math.round(Math.abs(dt.diffNow('seconds').seconds));
  if (seconds < 10) return t('common.justNow');
  return dt.toRelative({ locale: getLocale(lang), style: 'short' }) || t('common.justNow');
}

export function formatCalendarDayLabel(value: string, lang: string | null | undefined, t: TFunction): string {
  const date = DateTime.fromISO(value).setLocale(getLocale(lang));
  if (!date.isValid) return value;

  const today = DateTime.local().startOf('day');
  const tomorrow = today.plus({ days: 1 });

  if (date.hasSame(today, 'day')) return t('calendar.today');
  if (date.hasSame(tomorrow, 'day')) return t('calendar.tomorrow');

  return date.toLocaleString({ weekday: 'short', month: 'short', day: 'numeric' });
}

function formatHourMinute(hour?: number, minute?: number, lang?: string | null): string {
  if (typeof hour !== 'number' || typeof minute !== 'number') return '';
  const dt = DateTime.fromObject({ hour, minute }).setLocale(getLocale(lang));
  return dt.toLocaleString(DateTime.TIME_SIMPLE);
}

export function formatRecurrenceRule(value: string | null | undefined, lang?: string | null, untilUtc?: string | null): string {
  if (!value) return '';

  let recur: any;
  try {
    recur = typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return value;
  }

  if (!recur?.type) return '';

  const time = formatHourMinute(recur.hour, recur.minute, lang);
  const weekdayNames = {
    en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    fr: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'],
    ar: ['الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'],
    lb: ['tanen', 'taleta', 'arba3a', 'khamees', 'jem3a', 'sabt', 'ahad'],
  } as const;
  const ordinal = {
    en: { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', last: 'last' },
    fr: { 1: '1er', 2: '2e', 3: '3e', 4: '4e', last: 'dernier' },
    ar: { 1: 'أول', 2: 'ثاني', 3: 'ثالث', 4: 'رابع', last: 'آخر' },
    lb: { 1: 'awwal', 2: 'tani', 3: 'talet', 4: 'rabe3', last: 'akhir' },
  } as const;
  const monthNames = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    fr: ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'],
    ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
    lb: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  } as const;
  const language = (lang || 'en') as keyof typeof weekdayNames;
  const weekday = weekdayNames[language]?.[(recur.weekday || 1) - 1] || weekdayNames.en[(recur.weekday || 1) - 1];
  const ord = ordinal[language]?.[recur.ordinal as keyof typeof ordinal.en] || ordinal.en[recur.ordinal as keyof typeof ordinal.en];
  const month = monthNames[language]?.[(Number(recur.month) || 1) - 1] || monthNames.en[(Number(recur.month) || 1) - 1];
  const atTime = {
    en: time ? ` at ${time}` : '',
    fr: time ? ` a ${time}` : '',
    ar: time ? ` ${time}` : '',
    lb: time ? ` @ ${time}` : '',
  } as const;

  let base = '';

  if (recur.type === 'interval') {
    const amount = recur.interval || 1;
    const unit = recur.unit || 'day';
    if (language === 'ar') base = `كل ${amount} ${unit}`;
    else if (language === 'fr') base = `tous les ${amount} ${unit}${amount > 1 ? 's' : ''}`;
    else if (language === 'lb') base = `kel ${amount} ${unit}`;
    else base = `every ${amount} ${unit}${amount > 1 ? 's' : ''}`;
  } else if (recur.type === 'multiple_days') {
    const names = (recur.weekdays || []).map((item: number) => weekdayNames[language]?.[item - 1] || weekdayNames.en[item - 1]).join(language === 'ar' ? ' و ' : ', ');
    if (language === 'ar') base = `كل ${names}${atTime.ar}`;
    else if (language === 'fr') base = `chaque ${names}${atTime.fr}`;
    else if (language === 'lb') base = `kel ${names}${atTime.lb}`;
    else base = `every ${names}${atTime.en}`;
  } else if (recur.type === 'weekdays') {
    if (language === 'ar') base = `كل ايام الاسبوع${atTime.ar}`;
    else if (language === 'fr') base = `jours ouvres${atTime.fr}`;
    else if (language === 'lb') base = `kel weekday${atTime.lb}`;
    else base = `every weekday${atTime.en}`;
  } else if (recur.type === 'weekends') {
    if (language === 'ar') base = `كل عطلة نهاية الاسبوع${atTime.ar}`;
    else if (language === 'fr') base = `week-end${atTime.fr}`;
    else if (language === 'lb') base = `kel weekend${atTime.lb}`;
    else base = `every weekend${atTime.en}`;
  } else if (recur.type === 'daily') {
    if (language === 'ar') base = `كل يوم${atTime.ar}`;
    else if (language === 'fr') base = `chaque jour${atTime.fr}`;
    else if (language === 'lb') base = `kel yom${atTime.lb}`;
    else base = `every day${atTime.en}`;
  } else if (recur.type === 'weekly') {
    if (language === 'ar') base = `كل ${weekday}${atTime.ar}`;
    else if (language === 'fr') base = `chaque ${weekday}${atTime.fr}`;
    else if (language === 'lb') base = `kel ${weekday}${atTime.lb}`;
    else base = `every ${weekday}${atTime.en}`;
  } else if (recur.type === 'monthly_date') {
    if (language === 'ar') base = `كل شهر يوم ${recur.day}${atTime.ar}`;
    else if (language === 'fr') base = `chaque mois le ${recur.day}${atTime.fr}`;
    else if (language === 'lb') base = `kel shahr yom ${recur.day}${atTime.lb}`;
    else base = `every month on the ${recur.day}${atTime.en}`;
  } else if (recur.type === 'monthly_weekday' || recur.type === 'ordinal_monthly') {
    if (language === 'ar') base = `كل ${ord} ${weekday}${atTime.ar}`;
    else if (language === 'fr') base = `chaque ${ord} ${weekday}${atTime.fr}`;
    else if (language === 'lb') base = `kel ${ord} ${weekday}${atTime.lb}`;
    else base = `every ${ord} ${weekday}${atTime.en}`;
  } else if (recur.type === 'yearly_date') {
    if (language === 'ar') base = `كل سنة ${month} ${recur.day}${atTime.ar}`;
    else if (language === 'fr') base = `chaque annee le ${recur.day} ${month}${atTime.fr}`;
    else if (language === 'lb') base = `kel sana ${month} ${recur.day}${atTime.lb}`;
    else base = `every year on ${month} ${recur.day}${atTime.en}`;
  } else {
    return value;
  }

  base = base.trim();

  // Append exclude days
  if (recur.exclude?.length) {
    const dayNames = weekdayNames[language] || weekdayNames.en;
    const excluded = (recur.exclude as number[]).map(w => dayNames[w - 1]).filter(Boolean);
    if (language === 'ar') base += ` ما عدا ${excluded.join(' و ')}`;
    else if (language === 'lb') base += ` ma3ada ${excluded.join(' w ')}`;
    else if (language === 'fr') base += ` sauf ${excluded.join(', ')}`;
    else base += ` except ${excluded.join(', ')}`;
  }

  // Append until date
  if (untilUtc) {
    const dt = DateTime.fromISO(untilUtc);
    if (dt.isValid) {
      const label = dt.toLocaleString({ day: 'numeric', month: 'short', year: 'numeric' }, { locale: getLocale(lang) });
      if (language === 'ar') base += ` حتى ${label}`;
      else if (language === 'lb') base += ` la hadd ${label}`;
      else if (language === 'fr') base += ` jusqu'au ${label}`;
      else base += ` until ${label}`;
    }
  }

  return base;
}

/**
 * A compact, locale-aware elapsed duration for overdue reminders.
 * Unlike `formatRelativeTime`, this describes age ("3 months overdue")
 * instead of repeating a second date-like phrase ("3 mo. ago").
 */
export function formatOverdueAge(value: string | null | undefined, lang?: string | null): string {
  if (!value) return '';
  const dt = DateTime.fromISO(value);
  if (!dt.isValid) return '';

  const elapsedSeconds = Math.max(0, Math.floor(DateTime.local().diff(dt, 'seconds').seconds));
  const ranges: Array<{ seconds: number; unit: Intl.NumberFormatOptions['unit'] }> = [
    { seconds: 365 * 86400, unit: 'year' },
    { seconds: 30 * 86400, unit: 'month' },
    { seconds: 7 * 86400, unit: 'week' },
    { seconds: 86400, unit: 'day' },
    { seconds: 3600, unit: 'hour' },
    { seconds: 60, unit: 'minute' },
  ];
  const range = ranges.find((candidate) => elapsedSeconds >= candidate.seconds) ?? ranges[ranges.length - 1];
  const amount = Math.max(1, Math.floor(elapsedSeconds / range.seconds));
  const locale = getLocale(lang);

  let duration: string;
  try {
    duration = new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: range.unit,
      unitDisplay: 'long',
    }).format(amount);
  } catch {
    const unit = `${range.unit}${amount === 1 ? '' : 's'}`;
    duration = `${formatNumber(amount, lang)} ${unit}`;
  }

  if (locale === 'ar') return `متأخر ${duration}`;
  if (locale === 'fr') return `${duration} de retard`;
  if ((lang || '').toLowerCase().startsWith('lb')) return `${duration} met2akhkher`;
  return `${duration} overdue`;
}
